import {
  EnergyCheckState,
  EvaluationResult,
  Trigger,
} from '../types/energy.types';

/**
 * Evaluates energy check using Hallowell's methodology:
 * - Magnitude: How severe is the energy?
 * - Exposure: Can it reach a person?
 * - Controls: Are effective controls in place?
 *
 * Rating Logic:
 * - GREEN: No significant risk or excellent controls
 * - YELLOW: Risk present but manageable with controls
 * - RED: High magnitude + exposure + inadequate controls = STOP
 */
export function evaluateEnergyCheck(state: EnergyCheckState): EvaluationResult {
  const icons = state.selectedIcons;
  const a = state.answers;

  const triggers: Trigger[] = [];
  const controls = new Set<string>();

  let level: 'GREEN' | 'YELLOW' | 'RED' = 'GREEN';
  let hasUncertainty = false;

  const bump = (target: 'GREEN' | 'YELLOW' | 'RED') => {
    const rank = { GREEN: 0, YELLOW: 1, RED: 2 };
    if (rank[target] > rank[level]) level = target;
  };

  // If no icons selected
  if (icons.size === 0) {
    triggers.push({
      code: 'GREEN_NONE',
      text: 'No energy hazards identified',
    });
    controls.add('If unsure about hazards, consult supervisor before starting');
    return { level: 'GREEN', triggers, controls: Array.from(controls) };
  }

  // Base: any icon selected = at least YELLOW
  bump('YELLOW');

  // Helper to evaluate each energy type
  const evaluateEnergy = (
    magnitude: string,
    exposure: string,
    controlState: string,
  ): 'GREEN' | 'YELLOW' | 'RED' => {
    // Safety check - if any value is undefined/empty, treat as uncertainty
    if (!magnitude || !exposure || !controlState) {
      hasUncertainty = true;
      return 'YELLOW';
    }

    // Check for uncertainty
    if (magnitude === 'not_sure' || exposure === 'not_sure') {
      hasUncertainty = true;
      return 'YELLOW';
    }

    // No exposure = YELLOW (energy present but can't reach people)
    if (exposure.includes('no_')) {
      return 'YELLOW';
    }

    // Best controls (isolation/elimination) = YELLOW
    if (['isolation', 'elimination', 'comprehensive'].includes(controlState)) {
      return 'YELLOW';
    }

    // Physical barriers/prevention = YELLOW for most scenarios
    if (
      ['prevention', 'separation', 'guarding', 'shoring', 'restraint'].includes(
        controlState,
      )
    ) {
      // High/Severe + exposure + mid-level controls = still RED
      if (
        (magnitude === 'high' || magnitude === 'severe') &&
        exposure === 'yes'
      ) {
        return 'RED';
      }
      return 'YELLOW';
    }

    // Admin controls or procedures
    if (['procedures', 'admin', 'standard', 'basic'].includes(controlState)) {
      if (magnitude === 'low' || magnitude === 'medium') {
        return 'YELLOW';
      }
      return 'RED'; // High/severe + admin only = RED
    }

    // PPE only or no controls
    if (['ppe', 'none', 'awareness'].includes(controlState)) {
      if (magnitude === 'low' && exposure !== 'yes') {
        return 'YELLOW';
      }
      if (
        (magnitude === 'medium' ||
          magnitude === 'high' ||
          magnitude === 'severe') &&
        exposure === 'yes'
      ) {
        return 'RED';
      }
      return 'YELLOW';
    }

    return 'YELLOW';
  };

  // FALL FROM HEIGHT
  if (icons.has('fall')) {
    const mag = a.fall_magnitude;
    const exp = a.fall_exposure;
    const ctrl = a.fall_controls;
    const result = evaluateEnergy(mag, exp, ctrl);

    if (mag === 'not_sure' || exp === 'not_sure') {
      triggers.push({
        code: 'UNCERTAIN_FALL',
        text: 'Fall risk uncertain - confirm height and exposure',
      });
      controls.add('⚠️ Confirm fall height and access before starting');
      bump('YELLOW');
    } else if (result === 'RED') {
      triggers.push({
        code: 'RED_FALL',
        text: `Fall from height: ${mag} magnitude + people exposed + inadequate controls`,
      });
      controls.add(
        '🛑 STOP - Install fall prevention (barriers/edge protection) or fall arrest system',
      );
      controls.add('Obtain permit and supervisor approval before proceeding');
      bump('RED');
    } else if (
      (mag === 'severe' || mag === 'high') &&
      (ctrl === 'none' || ctrl === 'ppe')
    ) {
      // Severe/high fall + (no controls OR PPE only) = RED
      // Falls >2m need engineering controls (barriers) or elimination, not just harness
      if (ctrl === 'none') {
        triggers.push({
          code: 'RED_FALL_NO_CONTROLS',
          text: 'Severe/high fall risk with no verified controls',
        });
      } else {
        triggers.push({
          code: 'RED_FALL_PPE_ONLY',
          text: 'Severe/high fall - harness alone insufficient without rescue plan',
        });
      }
      controls.add(
        '🛑 STOP - Install fall prevention (guardrails/barriers) OR comprehensive fall arrest with rescue',
      );
      controls.add('Obtain permit and supervisor approval before proceeding');
      bump('RED');
    } else if (mag === 'severe' || mag === 'high') {
      triggers.push({
        code: 'YEL_FALL_HIGH',
        text: 'Serious fall potential - verify controls',
      });
      controls.add(
        'Fall protection plan required (guardrails or harness + rescue plan)',
      );
      bump('YELLOW');
    } else {
      controls.add(
        'Maintain stable footing and housekeeping (slip/trip prevention)',
      );
    }
  }

  // VEHICLE / MOBILE PLANT
  if (icons.has('vehicle')) {
    const mag = a.vehicle_magnitude;
    const exp = a.vehicle_exposure;
    const ctrl = a.vehicle_controls;
    const result = evaluateEnergy(mag, exp, ctrl);

    if (mag === 'not_sure' || exp === 'not_sure') {
      triggers.push({
        code: 'UNCERTAIN_VEHICLE',
        text: 'Vehicle risk uncertain - confirm separation',
      });
      controls.add('⚠️ Confirm vehicle separation and traffic controls');
      bump('YELLOW');
    } else if (result === 'RED') {
      triggers.push({
        code: 'RED_VEHICLE',
        text: `Mobile plant: ${mag} magnitude + people in line-of-fire + inadequate separation`,
      });
      controls.add(
        '🛑 STOP - Establish physical separation (barriers/exclusion zone)',
      );
      controls.add('Traffic management plan required with spotters/permits');
      bump('RED');
    } else if (
      (mag === 'severe' || mag === 'high') &&
      (ctrl === 'none' || ctrl === 'ppe')
    ) {
      // Severe/high vehicle + (no controls OR PPE only) = RED
      // Heavy/fast plant needs physical separation, not just hi-vis
      if (ctrl === 'none') {
        triggers.push({
          code: 'RED_VEHICLE_NO_CONTROLS',
          text: 'Heavy/fast plant with no traffic controls',
        });
      } else {
        triggers.push({
          code: 'RED_VEHICLE_PPE_ONLY',
          text: 'Heavy/fast plant - hi-vis alone insufficient',
        });
      }
      controls.add(
        '🛑 STOP - Establish physical separation (barriers/exclusion zone) or traffic management plan',
      );
      controls.add('Spotters/permits required for heavy plant operations');
      bump('RED');
    } else if (mag === 'severe' || mag === 'high') {
      triggers.push({
        code: 'YEL_VEHICLE_HIGH',
        text: 'Heavy/fast plant - verify separation',
      });
      controls.add(
        'Maintain positive separation and visibility - use spotters for manoeuvres',
      );
      bump('YELLOW');
    } else {
      controls.add('Stay visible to operators - use designated walkways');
    }
  }

  // SUSPENDED / LIFTED LOAD
  if (icons.has('suspended')) {
    const mag = a.suspended_magnitude;
    const exp = a.suspended_exposure;
    const ctrl = a.suspended_controls;
    const result = evaluateEnergy(mag, exp, ctrl);

    if (mag === 'not_sure' || exp === 'not_sure') {
      triggers.push({
        code: 'UNCERTAIN_SUSPENDED',
        text: 'Lifting risk uncertain - confirm load path',
      });
      controls.add('⚠️ Confirm load weight and exclusion zone');
      bump('YELLOW');
    } else if (result === 'RED') {
      triggers.push({
        code: 'RED_SUSPENDED',
        text: `Suspended load: ${mag} magnitude + people exposed + inadequate controls`,
      });
      controls.add('🛑 STOP - No personnel under suspended loads');
      controls.add(
        'Lift plan required with rated gear, competent operator, and exclusion zone',
      );
      bump('RED');
    } else if (
      (mag === 'severe' || mag === 'high') &&
      (ctrl === 'none' || ctrl === 'basic')
    ) {
      // Severe/high lifting + (no controls OR basic only) = RED
      // Heavy/complex lifts need comprehensive lift plan, not just rated gear
      if (ctrl === 'none') {
        triggers.push({
          code: 'RED_SUSPENDED_NO_CONTROLS',
          text: 'Heavy/complex lift with no lifting plan',
        });
      } else {
        triggers.push({
          code: 'RED_SUSPENDED_BASIC_ONLY',
          text: 'Heavy/complex lift - rated gear alone insufficient',
        });
      }
      controls.add('🛑 STOP - Formal lift plan required with exclusion zone');
      controls.add(
        'Lift plan required with rated gear, competent operator, and exclusion zone',
      );
      bump('RED');
    } else if (exp === 'yes' || mag === 'severe' || mag === 'high') {
      triggers.push({
        code: 'YEL_SUSPENDED_HIGH',
        text: 'Significant lifting operation - verify controls',
      });
      controls.add(
        'Verify lifting gear inspection status - maintain exclusion zone',
      );
      bump('YELLOW');
    } else {
      controls.add('Keep clear of load path - verify rated lifting gear');
    }
  }

  // STORED PRESSURE
  if (icons.has('pressure')) {
    const mag = a.pressure_magnitude;
    const exp = a.pressure_exposure;
    const ctrl = a.pressure_controls;
    const result = evaluateEnergy(mag, exp, ctrl);

    if (mag === 'not_sure' || exp === 'not_sure') {
      triggers.push({
        code: 'UNCERTAIN_PRESSURE',
        text: 'Pressure system risk uncertain',
      });
      controls.add('⚠️ Confirm system pressure and release potential');
      bump('YELLOW');
    } else if (result === 'RED') {
      triggers.push({
        code: 'RED_PRESSURE',
        text: `Pressurised system: ${mag} magnitude + release could impact person + not isolated`,
      });
      controls.add(
        '🛑 STOP - Depressurise, bleed down, and LOTO before breaking containment',
      );
      controls.add(
        'Use rated hoses/fittings - wear face/eye PPE - stand clear on re-pressurisation',
      );
      bump('RED');
    } else if (
      (mag === 'severe' || mag === 'high') &&
      (ctrl === 'none' || ctrl === 'ppe')
    ) {
      // Severe/high pressure + (no controls OR PPE only) = RED
      // High pressure needs depressurization/isolation, not just face shields
      if (ctrl === 'none') {
        triggers.push({
          code: 'RED_PRESSURE_NO_CONTROLS',
          text: 'High pressure system with no isolation',
        });
      } else {
        triggers.push({
          code: 'RED_PRESSURE_PPE_ONLY',
          text: 'High pressure - PPE alone insufficient',
        });
      }
      controls.add(
        '🛑 STOP - Depressurise, bleed down, and LOTO before breaking containment',
      );
      controls.add(
        'Use rated hoses/fittings - stand clear on re-pressurisation',
      );
      bump('RED');
    } else if (mag === 'severe' || mag === 'high') {
      triggers.push({
        code: 'YEL_PRESSURE_HIGH',
        text: 'High pressure system - verify isolation',
      });
      controls.add(
        'Confirm pressure rating - isolate before breaking containment',
      );
      bump('YELLOW');
    } else {
      controls.add('Use rated components - verify system integrity before use');
    }
  }

  // ELECTRICAL EXPOSURE
  if (icons.has('electrical')) {
    const mag = a.electrical_magnitude;
    const exp = a.electrical_exposure;
    const ctrl = a.electrical_controls;
    const result = evaluateEnergy(mag, exp, ctrl);

    if (mag === 'not_sure' || exp === 'not_sure') {
      triggers.push({
        code: 'UNCERTAIN_ELECTRICAL',
        text: 'Electrical risk uncertain - treat as live',
      });
      controls.add(
        '⚠️ Assume live until confirmed isolated - get authorised person',
      );
      bump('YELLOW');
    } else if (result === 'RED') {
      triggers.push({
        code: 'RED_ELECTRICAL',
        text: `Electrical: ${mag} voltage + contact possible + not isolated`,
      });
      controls.add(
        '🛑 STOP - Isolate, LOTO, and test for dead (authorised person only)',
      );
      controls.add('Use insulated tools/PPE - establish exclusion zone');
      bump('RED');
    } else if (
      (mag === 'severe' || mag === 'high') &&
      (ctrl === 'none' || ctrl === 'ppe')
    ) {
      // Severe/high voltage + (no controls OR PPE only) = RED
      // High voltage needs isolation/LOTO, not just insulated gloves
      if (ctrl === 'none') {
        triggers.push({
          code: 'RED_ELECTRICAL_NO_CONTROLS',
          text: 'High voltage with no isolation controls',
        });
      } else {
        triggers.push({
          code: 'RED_ELECTRICAL_PPE_ONLY',
          text: 'High voltage - insulated PPE alone insufficient',
        });
      }
      controls.add(
        '🛑 STOP - Isolate, LOTO, and test for dead (authorised person only)',
      );
      controls.add('Arc flash assessment required for high voltage work');
      bump('RED');
    } else if (mag === 'severe' || mag === 'high') {
      triggers.push({
        code: 'YEL_ELECTRICAL_HIGH',
        text: 'High voltage - verify isolation',
      });
      controls.add('Confirm isolation and test for dead before approach');
      bump('YELLOW');
    } else {
      controls.add('Verify isolation status before work - use appropriate PPE');
    }
  }

  // ROTATING / MOVING MACHINERY
  if (icons.has('machinery')) {
    const mag = a.machinery_magnitude;
    const exp = a.machinery_exposure;
    const ctrl = a.machinery_controls;
    const result = evaluateEnergy(mag, exp, ctrl);

    if (mag === 'not_sure' || exp === 'not_sure') {
      triggers.push({
        code: 'UNCERTAIN_MACHINERY',
        text: 'Machinery risk uncertain',
      });
      controls.add('⚠️ Confirm guarding and isolation before access');
      bump('YELLOW');
    } else if (result === 'RED') {
      triggers.push({
        code: 'RED_MACHINERY',
        text: `Machinery: ${mag} force + moving parts accessible + inadequate guarding`,
      });
      controls.add(
        '🛑 STOP - Guarding must be in place OR isolate/LOTO before access',
      );
      controls.add('Do not bypass or remove guards while machine is running');
      bump('RED');
    } else if (
      (mag === 'severe' || mag === 'high') &&
      (ctrl === 'none' || ctrl === 'ppe')
    ) {
      // Severe/high force machinery + (no controls OR PPE only) = RED
      // High force machinery needs guarding or isolation, not just awareness
      if (ctrl === 'none') {
        triggers.push({
          code: 'RED_MACHINERY_NO_CONTROLS',
          text: 'High-force machinery with no guarding/isolation',
        });
      } else {
        triggers.push({
          code: 'RED_MACHINERY_PPE_ONLY',
          text: 'High-force machinery - awareness alone insufficient',
        });
      }
      controls.add(
        '🛑 STOP - Install interlocked guarding OR isolate/LOTO before access',
      );
      controls.add('Do not bypass or remove guards while machine is running');
      bump('RED');
    } else if (mag === 'severe' || mag === 'high') {
      triggers.push({
        code: 'YEL_MACHINERY_HIGH',
        text: 'High-force machinery - verify guarding',
      });
      controls.add('Confirm interlocked guarding remains effective');
      bump('YELLOW');
    } else {
      controls.add('Keep clear of moving parts - verify guarding before use');
    }
  }

  // STORED MECHANICAL ENERGY
  if (icons.has('stored_mech')) {
    const mag = a.stored_mech_magnitude;
    const exp = a.stored_mech_exposure;
    const ctrl = a.stored_mech_controls;
    const result = evaluateEnergy(mag, exp, ctrl);

    if (mag === 'not_sure' || exp === 'not_sure') {
      triggers.push({
        code: 'UNCERTAIN_STORED',
        text: 'Stored energy risk uncertain',
      });
      controls.add('⚠️ Confirm tension/compression level and strike path');
      bump('YELLOW');
    } else if (result === 'RED') {
      triggers.push({
        code: 'RED_STORED',
        text: `Stored mechanical: ${mag} magnitude + could strike person + not restrained`,
      });
      controls.add('🛑 STOP - Restrain/secure or fully release before work');
      controls.add('Stay out of snap-back path - establish exclusion zone');
      bump('RED');
    } else if (
      (mag === 'severe' || mag === 'high') &&
      (ctrl === 'none' || ctrl === 'awareness')
    ) {
      // Severe/high stored energy + (no controls OR awareness only) = RED
      // High tension/compression needs restraint or controlled release, not just awareness
      if (ctrl === 'none') {
        triggers.push({
          code: 'RED_STORED_NO_CONTROLS',
          text: 'High stored energy with no restraint/release controls',
        });
      } else {
        triggers.push({
          code: 'RED_STORED_AWARENESS_ONLY',
          text: 'High stored energy - awareness alone insufficient',
        });
      }
      controls.add('🛑 STOP - Restrain/secure or fully release before work');
      controls.add(
        'Use controlled release method - stay clear of snap-back path',
      );
      bump('RED');
    } else if (mag === 'severe' || mag === 'high') {
      triggers.push({
        code: 'YEL_STORED_HIGH',
        text: 'High stored energy - verify restraint',
      });
      controls.add(
        'Use controlled release method - stay clear of potential release paths',
      );
      bump('YELLOW');
    } else {
      controls.add('Identify and avoid snap-back zones');
    }
  }

  // EXCAVATION / COLLAPSE
  if (icons.has('excavation')) {
    const mag = a.excavation_magnitude;
    const exp = a.excavation_exposure;
    const ctrl = a.excavation_controls;
    const result = evaluateEnergy(mag, exp, ctrl);

    if (mag === 'not_sure' || exp === 'not_sure') {
      triggers.push({
        code: 'UNCERTAIN_EXCAVATION',
        text: 'Excavation risk uncertain',
      });
      controls.add('⚠️ Confirm depth, soil type, and entry requirements');
      bump('YELLOW');
    } else if (result === 'RED') {
      triggers.push({
        code: 'RED_EXCAVATION',
        text: `Excavation: ${mag} depth + entry required + inadequate protection`,
      });
      controls.add(
        '🛑 STOP - Shoring/benching/trench box required before entry',
      );
      controls.add(
        'Permit required with competent person assessment and services located',
      );
      bump('RED');
    } else if (
      (mag === 'severe' || mag === 'high') &&
      (ctrl === 'none' || ctrl === 'basic') &&
      exp === 'yes'
    ) {
      // Severe/high excavation + entry required + (no controls OR basic only) = RED
      // Deep excavations with entry need shoring/protection systems, not just edge protection
      if (ctrl === 'none') {
        triggers.push({
          code: 'RED_EXCAVATION_NO_CONTROLS',
          text: 'Deep excavation with entry but no protection system',
        });
      } else {
        triggers.push({
          code: 'RED_EXCAVATION_BASIC_ONLY',
          text: 'Deep excavation with entry - edge protection alone insufficient',
        });
      }
      controls.add(
        '🛑 STOP - Shoring/benching/trench box required before entry',
      );
      controls.add(
        'Permit required with competent person assessment and services located',
      );
      bump('RED');
    } else if (exp === 'yes' || mag === 'severe' || mag === 'high') {
      triggers.push({
        code: 'YEL_EXCAVATION_HIGH',
        text: 'Deep excavation - verify protection',
      });
      controls.add(
        'Edge protection and spoil management - keep loads back from edges',
      );
      bump('YELLOW');
    } else {
      controls.add('Maintain edge protection - locate services before digging');
    }
  }

  // CHEMICAL / THERMAL (tag only)
  if (icons.has('chemical_thermal')) {
    bump('YELLOW');
    triggers.push({
      code: 'TAG_CHEM_THERM',
      text: 'Chemical/thermal hazard present - assess separately',
    });
    controls.add(
      'Check SDS for chemical hazards - ensure ventilation/PPE as required',
    );
    controls.add(
      'Assess burn/heat/cold exposure - implement temperature controls',
    );
  }

  // BIOLOGICAL HAZARD
  if (icons.has('biological')) {
    const mag = a.biological_magnitude;
    const exp = a.biological_exposure;
    const ctrl = a.biological_controls;
    const result = evaluateEnergy(mag, exp, ctrl);

    if (mag === 'not_sure' || exp === 'not_sure') {
      triggers.push({
        code: 'UNCERTAIN_BIOLOGICAL',
        text: 'Biological risk uncertain - assess contamination level',
      });
      controls.add(
        '⚠️ Confirm contamination status and required PPE/hygiene protocols',
      );
      bump('YELLOW');
    } else if (result === 'RED') {
      triggers.push({
        code: 'RED_BIOLOGICAL',
        text: `Biological: ${mag} hazard + contact possible + inadequate controls`,
      });
      controls.add(
        '🛑 STOP - Implement engineering controls (containment/ventilation)',
      );
      controls.add(
        'PPE required: appropriate gloves, respiratory protection, hygiene protocols',
      );
      controls.add('Verify vaccination status if applicable');
      bump('RED');
    } else if (
      (mag === 'severe' || mag === 'high') &&
      (ctrl === 'none' || ctrl === 'ppe')
    ) {
      // Special case: severe/high biological + (no controls OR PPE only) = RED
      // Even if exposure says "no", severe bio hazards need verified engineering/admin controls
      // PPE alone is insufficient for severe/high biological hazards
      if (ctrl === 'none') {
        triggers.push({
          code: 'RED_BIOLOGICAL_NO_CONTROLS',
          text: 'Severe/high biological hazard with no verified controls',
        });
      } else {
        triggers.push({
          code: 'RED_BIOLOGICAL_PPE_ONLY',
          text: 'Severe/high biological hazard - PPE alone insufficient',
        });
      }
      controls.add(
        '🛑 STOP - Implement engineering controls (ventilation/containment/decontamination)',
      );
      controls.add(
        'Establish formal hygiene protocols and vaccination verification',
      );
      controls.add(
        'PPE required: appropriate gloves, respiratory protection, hygiene protocols',
      );
      controls.add(
        'Confirm decontamination procedures and waste disposal requirements',
      );
      bump('RED');
    } else if (mag === 'severe' || mag === 'high') {
      triggers.push({
        code: 'YEL_BIOLOGICAL_HIGH',
        text: 'Significant biological hazard - verify controls',
      });
      controls.add(
        'Confirm decontamination procedures and waste disposal requirements',
      );
      controls.add(
        'Implement strict hygiene protocols - wash facilities required',
      );
      bump('YELLOW');
    } else {
      controls.add('Practice good hygiene - wash hands after work');
      controls.add('Use appropriate PPE for environment');
    }
  }

  // Add uncertainty warning if applicable
  if (hasUncertainty) {
    triggers.unshift({
      code: 'UNCERTAINTY_WARNING',
      text: '⚠️ Some answers marked "Not sure" - escalate if uncertain',
    });
    controls.add(
      '❗ Get supervisor input before proceeding if any uncertainty remains',
    );
  }

  // If still YELLOW with no explicit triggers, add generic
  // TS incorrectly narrows level here - use string comparison to bypass
  if (String(level) === 'YELLOW' && triggers.length === 0) {
    triggers.push({
      code: 'YEL_ENERGY_PRESENT',
      text: 'Energy present - manageable with controls',
    });
  }

  return { level, triggers, controls: Array.from(controls) };
}
